import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { DashboardSnapshot } from "../../shared/contracts";
import { api, getErrorMessage } from "../lib/api";

interface DashboardContextValue {
  dashboard: DashboardSnapshot | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  replaceDashboard: (snapshot: DashboardSnapshot) => void;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

function isAbortError(caught: unknown) {
  return caught instanceof DOMException
    ? caught.name === "AbortError"
    : caught instanceof Error && caught.name === "AbortError";
}

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [dashboard, setDashboard] = useState<DashboardSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasDashboard = useRef(false);
  const requestSequence = useRef(0);
  const activeController = useRef<AbortController | null>(null);

  const beginRequest = useCallback(() => {
    const sequence = ++requestSequence.current;
    activeController.current?.abort();
    const controller = new AbortController();
    activeController.current = controller;
    return { sequence, controller };
  }, []);

  const isCurrent = useCallback((sequence: number) => requestSequence.current === sequence, []);

  const refresh = useCallback(async () => {
    const { sequence, controller } = beginRequest();
    if (hasDashboard.current) setRefreshing(true);
    else setLoading(true);
    try {
      const snapshot = await api.dashboard(controller.signal);
      if (!isCurrent(sequence)) return;
      setDashboard(snapshot);
      hasDashboard.current = true;
      setError(null);
    } catch (caught) {
      if (!isAbortError(caught) && isCurrent(sequence)) setError(getErrorMessage(caught));
    } finally {
      if (isCurrent(sequence)) {
        activeController.current = null;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [beginRequest, isCurrent]);

  useEffect(() => {
    let active = true;
    const { sequence, controller } = beginRequest();
    api.dashboard(controller.signal)
      .then((snapshot) => {
        if (!active || !isCurrent(sequence)) return;
        setDashboard(snapshot);
        hasDashboard.current = true;
        setError(null);
      })
      .catch((caught: unknown) => {
        if (active && !isAbortError(caught) && isCurrent(sequence)) setError(getErrorMessage(caught));
      })
      .finally(() => {
        if (active && isCurrent(sequence)) {
          activeController.current = null;
          setLoading(false);
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [beginRequest, isCurrent]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  const replaceDashboard = useCallback((snapshot: DashboardSnapshot) => {
    requestSequence.current += 1;
    activeController.current?.abort();
    activeController.current = null;
    setDashboard(snapshot);
    hasDashboard.current = true;
    setError(null);
    setLoading(false);
    setRefreshing(false);
  }, []);

  const value = useMemo<DashboardContextValue>(
    () => ({
      dashboard,
      loading,
      refreshing,
      error,
      refresh,
      replaceDashboard,
    }),
    [dashboard, error, loading, refresh, refreshing, replaceDashboard],
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) throw new Error("useDashboard must be used inside DashboardProvider");
  return context;
}
